/**
 * LoadFootprintWaterfall — the single reconciling account of the load's
 * footprint, and the only place the grid-electricity number appears.
 *
 * The old dashboard led with a big red "Grid-Supplied Electricity Footprint"
 * card AND re-listed the same grid number inside the net card, so a customer
 * saw the scariest figure twice with no way to tell whether it was
 * double-counted. This walks from zero to the net in signed steps that add up
 * exactly, for whichever lens is active:
 *
 *     Grid electricity        −  harm
 *     On-site natural gas     −  harm
 *     Renewable build         −  harm
 *     Avoided on-site gas     +  benefit
 *     ─────────────────────────────────
 *     Net                     =  sum
 *
 * Sign convention matches the rest of the load surface: positive = net benefit,
 * negative = net burden. Steps are contributions TO the net, so harm terms are
 * negative and the RE displacement credit is positive.
 *
 * The avoided-grid-import counterfactual (`grid_avoided_by_onsite`) is
 * deliberately NOT a step. It is real but it is already expressed as a smaller
 * grid-electricity line — counting it again would double the RE benefit, which
 * is exactly the bug this view exists to kill. It renders as a footnote below
 * the rule instead.
 */

import { useState } from "react";
import type {
  ImpactValues,
  GridAvoidedImpact,
} from "../../load-portfolio/domain/types";
import { toSigFigs, formatImpact } from "../../../utils/format";
import { fmtMWh } from "../fuel";

type Endpoint = "dalys" | "pdfs" | "usd";

export interface LoadFootprintWaterfallProps {
  /** Lens-appropriate blocks (consequential or attributional). */
  gridSupply?: ImpactValues | null;
  ngImpacts: ImpactValues;
  reBenefits: ImpactValues;
  reInfra: ImpactValues;
  net: ImpactValues;
  /** Disclosure only — never a step. */
  gridAvoided?: GridAvoidedImpact | null;
  hasGridSupply: boolean;
  lens: "consequential" | "attributional";
}

const ENDPOINTS: Array<{ key: Endpoint; label: string; unit: string }> = [
  { key: "dalys", label: "Human health", unit: "DALYs" },
  { key: "pdfs", label: "Ecosystem", unit: "species·yr" },
  { key: "usd", label: "Economic", unit: "USD" },
];

const pick = (v: ImpactValues | GridAvoidedImpact, e: Endpoint) =>
  e === "dalys" ? v.DALYs : e === "pdfs" ? v.PDFs : v.USD;

const fmt = (n: number, e: Endpoint) =>
  e === "usd" ? formatImpact(n, "usd") : toSigFigs(n);

interface Step {
  label: string;
  hint: string;
  /** Signed contribution to the net. */
  value: number;
}

export function LoadFootprintWaterfall(props: LoadFootprintWaterfallProps) {
  const {
    gridSupply,
    ngImpacts,
    reBenefits,
    reInfra,
    net,
    gridAvoided,
    hasGridSupply,
    lens,
  } = props;
  const [endpoint, setEndpoint] = useState<Endpoint>("dalys");
  const spec = ENDPOINTS.find((e) => e.key === endpoint)!;

  const steps: Step[] = [];
  if (hasGridSupply && gridSupply) {
    steps.push({
      label: "Grid electricity",
      hint:
        lens === "attributional"
          ? "the average grid mix that served the residual load"
          : "the marginal units the grid ramped to serve the residual load",
      value: -pick(gridSupply, endpoint),
    });
  }
  if (pick(ngImpacts, endpoint) !== 0) {
    steps.push({
      label: "On-site natural gas",
      hint: "damages from running the on-site gas units",
      value: -pick(ngImpacts, endpoint),
    });
  }
  if (pick(reInfra, endpoint) !== 0) {
    steps.push({
      label: "Renewable build",
      hint: "lifecycle impacts of manufacturing and installing the on-site RE",
      value: -pick(reInfra, endpoint),
    });
  }
  if (pick(reBenefits, endpoint) !== 0) {
    steps.push({
      label: "Avoided on-site gas",
      hint: "gas the on-site renewables and storage displaced",
      value: pick(reBenefits, endpoint),
    });
  }

  const netValue = pick(net, endpoint);

  // Running cumulative path from 0 → net, so each bar floats between the
  // previous total and the new one (a true waterfall, not a bar chart).
  let running = 0;
  const bars = steps.map((s) => {
    const from = running;
    running += s.value;
    return { ...s, from, to: running };
  });

  const points = [0, netValue, ...bars.flatMap((b) => [b.from, b.to])];
  const lo = Math.min(...points);
  const hi = Math.max(...points);
  const span = hi - lo || 1;
  const pctOf = (v: number) => ((v - lo) / span) * 100;
  const zeroPct = pctOf(0);

  if (steps.length === 0) {
    return (
      <div className="card">
        <div className="ch">
          <h2>How we get to the net</h2>
        </div>
        <div className="cb">
          <div className="empty">
            No impact terms to reconcile for this run yet.
          </div>
        </div>
      </div>
    );
  }

  const avoidedValue = gridAvoided ? pick(gridAvoided, endpoint) : 0;
  const avoidedMwh = gridAvoided?.meta?.grid_import_mwh;

  return (
    <div className="card">
      <div className="ch">
        <h2>How we get to the net</h2>
        <div className="seg ghost" role="group" aria-label="Endpoint">
          {ENDPOINTS.map((e) => (
            <button
              key={e.key}
              aria-pressed={endpoint === e.key}
              onClick={() => setEndpoint(e.key)}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>
      <div className="cb">
        <p className="wf-intro">
          Every term that moves this load's footprint, in {spec.unit}. Harm pulls
          down, avoided harm pushes up, and the steps add up exactly to the net —
          the grid-electricity figure appears here once and nowhere else.
        </p>

        <div className="wf">
          {bars.map((b) => {
            const benefit = b.value > 0;
            const left = Math.min(pctOf(b.from), pctOf(b.to));
            const width = Math.abs(pctOf(b.to) - pctOf(b.from));
            return (
              <div className="wf-row" key={b.label}>
                <div className="wf-lab">
                  <span className="nm">{b.label}</span>
                  <span className="hn">{b.hint}</span>
                </div>
                <div className="wf-trk">
                  <span className="wf-zero" style={{ left: `${zeroPct}%` }} />
                  <span
                    className={`wf-bar ${benefit ? "ben" : "harm"}`}
                    style={{ left: `${left}%`, width: `${Math.max(width, 0.4)}%` }}
                  />
                </div>
                <div className={`wf-val ${benefit ? "ben" : "harm"}`}>
                  {b.value > 0 ? "+" : "−"}
                  {fmt(Math.abs(b.value), endpoint)}
                </div>
              </div>
            );
          })}

          <div className="wf-row net">
            <div className="wf-lab">
              <span className="nm">Net</span>
              <span className="hn">
                {netValue >= 0
                  ? "this load leaves the world better off on this endpoint"
                  : "this load carries a net burden on this endpoint"}
              </span>
            </div>
            <div className="wf-trk">
              <span className="wf-zero" style={{ left: `${zeroPct}%` }} />
              <span
                className={`wf-bar ${netValue >= 0 ? "ben" : "harm"}`}
                style={{
                  left: `${Math.min(pctOf(0), pctOf(netValue))}%`,
                  width: `${Math.max(Math.abs(pctOf(netValue) - pctOf(0)), 0.4)}%`,
                }}
              />
            </div>
            <div className={`wf-val ${netValue >= 0 ? "ben" : "harm"}`}>
              {netValue > 0 ? "+" : netValue < 0 ? "−" : ""}
              {fmt(Math.abs(netValue), endpoint)}
            </div>
          </div>
        </div>

        {gridAvoided && avoidedValue > 0 && (
          <div className="wf-note">
            <b>Not counted above:</b> on-site renewables and storage kept{" "}
            {avoidedMwh != null ? <b>{fmtMWh(avoidedMwh)}</b> : "energy"} off the
            grid, avoiding roughly {fmt(avoidedValue, endpoint)} {spec.unit} of{" "}
            {lens === "attributional" ? "average-grid" : "marginal-grid"} harm.
            That benefit is already reflected in the smaller grid-electricity
            line above — adding it again would count it twice.
          </div>
        )}
      </div>
    </div>
  );
}

export default LoadFootprintWaterfall;
